import {memo, useCallback} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import {Tooltip} from '@mui/material';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import {PATIENT_TAGS} from '../../../../../util/patientUtil';
import {useSessionStyles} from './useSessionStyles';

const PatientRowItem = memo(({ patient, onSelectPatient, onRefReset }) => {
  const classes = useSessionStyles();
  const { t } = useTranslation();

  const getData = useCallback((type, pt, classes) => {
    const getGenderIcon = (gender) => {
      function icon(gender) {
        switch (gender) {
          case 'Male':
            return <MaleIcon fontSize="small"/>;
          case 'Female':
            return <FemaleIcon fontSize="small"/>;
          default:
            return <QuestionMarkIcon fontSize="small"/>;
        }
      }

      return (
        <Tooltip title={gender}>
          <div className={clsx(classes.gender, gender)}>
            {icon(gender)}
          </div>
        </Tooltip>
      );
    };

    const getPtAge = (dob, rowData) => {
      let ageAsText = 'UNK';
      if (dob) {
        const age = moment.duration(moment(rowData?.admission?.startDate).diff(moment(dob))).asYears();
        if (age < 1) {
          const age = moment.duration(moment(rowData?.admission?.startDate).diff(moment(dob))).asMonths();
          ageAsText = Math.floor(age) + 'M';
        } else {
          ageAsText = Math.floor(age) + '';
        }
      }
      return ageAsText;
    };

    const getFullPatient = (pt) => {
      const patientInfo = pt?.patient ? pt?.patient : pt;
      const dncpr = patientInfo?.tags?.includes(PATIENT_TAGS.DO_NOT_CPR);

      return <div className={clsx(classes.fullRow, dncpr && classes.dncpr, classes.gRow)}>
        <div>
          <span className={classes.ptName}>{patientInfo?.familyName || ''},</span> {patientInfo?.firstName || ''}
        </div>

        <div className={classes.divRow}>
          {pt?.patient?.gender && getGenderIcon(pt?.patient?.gender)}
          <span className={clsx(classes.age, dncpr && 'dncpr')}>Age: {getPtAge(patientInfo?.dob, patientInfo)}</span>
        </div>
      </div>
    }

    const getPtNumbers = (pt) => {
      return (
        <div className={clsx(classes.fullRow, classes.gRow, 'medium')}>
          <div className={classes.spaceBetween} >
            <span className={classes.age} style={{minWidth: 50}}>Hos. No:</span>
            <span className={classes.cursor}>{pt?.hospitalNumber || '-'}</span>
          </div>

          <div className={classes.spaceBetween} >
            <span className={classes.age} style={{minWidth: 50}}>NHS No:</span>
            <span className={classes.cursor}>{pt?.nhsNumber || '-'}</span>
          </div>
        </div>
      );
    };

    switch (type) {
      case 'ptName':
        return getFullPatient(pt);
      case 'ptNumbers':
        return getPtNumbers(pt?.patient ? pt?.patient : pt);
      case 'bed':
      case 'zone':
      default:
        return;
    }
  }, []);

  return (
    <Grid item xs={12} className={classes.patientBox}>
      {getData('ptName', patient, classes)}
      {getData('ptNumbers', patient, classes)}

      <AgyleButton
        variant="contained"
        style={{marginLeft: 'auto'}}
        onClick={onRefReset ? onRefReset.bind(this, null) : onSelectPatient.bind(this, patient)}
      >
        {t(onRefReset ? "Discard" : "Select")}
      </AgyleButton>
    </Grid>
  )
});

PatientRowItem.propTypes = {
  patient: PropTypes.object.isRequired,
  onRefReset: PropTypes.func,
  onSelectPatient: PropTypes.func,
}

export default PatientRowItem;
